/**
 * Putting the gardener's preferences on the page: the theme's class on
 * <html>, the root font size, and the language.
 *
 * The one place the retired theme names are known (normaliseTheme). The
 * theme class is set here and, from the same saved preference, by
 * ThemeProvider; both clear the old names first, so they agree. The theme
 * is also kept on the device, so the next load can paint in it before the
 * account's preferences arrive.
 * The language is kept on the device the same way (keepStartLanguage; B72),
 * so the next load starts in the account's language.
 *
 * Who uses it: UserConfigProvider (applyPreferences on load and on every
 * change, getSavedTheme for the first paint) and ThemeProvider
 * (normaliseTheme).
 *
 * Storage is read and written inside try: a browser with site data blocked
 * throws on any touch of localStorage, and a theme that cannot be remembered
 * must still be applied.
 *
 * @module themeUtils
 */

import { Preferences } from '@/shared/context/userConfigStore';
import { setLanguage } from '@/shared/i18n/i18nSetup';
import { keepStartLanguage } from '@/shared/lib/startLanguage';
import { debugLog } from '@/config/config';
import { THEME_STORAGE_KEY as THEME_KEY } from '@/config/tunables/shared';
import { growSetting } from '@/shared/lib/growSettings';

/** The Grow setting that says the root size, in pixels, of each text size (ui.ROOT_FONT_*_PX; A67). */
const ROOT_FONT_SETTING = {
  small: 'ui.ROOT_FONT_SMALL_PX',
  medium: 'ui.ROOT_FONT_MEDIUM_PX',
  large: 'ui.ROOT_FONT_LARGE_PX',
} as const;

/** Key used to store the current theme in localStorage */
export const THEME_STORAGE_KEY = THEME_KEY;

/**
 * The theme kept on this device, or null when there is none or storage is
 * refused.
 *
 * @returns The stored value, unchecked.
 */
function readStoredTheme(): string | null {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Applies a theme to the document by manipulating CSS classes and saves it to localStorage.
 *
 * @param requested - The theme to apply ('grow', 'light', 'dark' or 'fieldnotes'); anything else is Grow.
 */
export function applyTheme(requested: Preferences['theme']): void {
  // Whatever was saved — including a retired name, or nothing — becomes a
  // theme this app still has. This is the one place the class is set, so
  // it is the one place that has to know.
  const theme = normaliseTheme(requested);
  debugLog('Theme', 'Applying theme:', {
    theme,
    currentClasses: document.documentElement.classList.toString(),
    currentTheme: readStoredTheme()
  });

  // Remove all theme classes first
  document.documentElement.classList.remove('grow', 'light', 'dark', 'fieldnotes', 'garden', 'almanac');
  
  // Apply the selected theme
  document.documentElement.classList.add(theme);
  
  // Handle dark mode for Tailwind separately
  if (theme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }

  debugLog('Theme', 'After theme application:', {
    theme,
    newClasses: document.documentElement.classList.toString(),
    storedTheme: readStoredTheme()
  });

  // Save theme to localStorage for persistence
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage refused: the theme is on the page, it just will not be remembered.
  }
}

/**
 * Applies a font size to the document root element.
 *
 * @param fontSize - The font size to apply ('small', 'medium', or 'large')
 */
export function applyFontSize(fontSize: Preferences['fontSize']): void {
  /*
   * Every size in the app is in rem, so this one number scales all of it.
   * The steps used to be 14, 16 and 18: "large" was twelve percent bigger
   * than medium, and with most labels set at text-xs and text-sm that left
   * them near thirteen pixels on a phone held at arm's length in the sun.
   * Medium is a notch up on the browser default now (17), and large (19) is a real
   * step. Small is 15. These are the owner's to change (Grow settings, ui.ROOT_FONT_*_PX,
   * kept from the last visit); on a very first visit, before the app has heard them, the
   * browser's own size stands, and the size is applied when they arrive (UserConfigProvider).
   */
  const pixels = growSetting(ROOT_FONT_SETTING[fontSize] ?? ROOT_FONT_SETTING.medium);
  document.documentElement.style.fontSize = pixels === undefined ? '' : `${pixels}px`;
}

/**
 * Applies all user preferences to the document: theme, font size and language.
 *
 * @param preferences - The preferences as saved (or the defaults before there are any).
 */
export function applyPreferences(preferences: Preferences): void {
  applyTheme(preferences.theme);
  applyFontSize(preferences.fontSize);
  // Language was missing here, so a saved Spanish preference took effect when
  // it was chosen and then reverted to English on the next page load.
  setLanguage(preferences.language);
  // The next cold load starts in it, so the first draw is not in the browser's language (B72).
  keepStartLanguage(preferences.language);
}

/**
 * Retrieves the saved theme from localStorage.
 *
 * @returns The saved theme or null if no theme is saved
 */
export function getSavedTheme(): Preferences['theme'] | null {
  const saved = readStoredTheme();
  return saved === null ? null : normaliseTheme(saved);
}

/**
 * The theme to actually apply for whatever was saved.
 *
 * Two names have retired — "garden", and "almanac", which became the
 * default and took the app's own name — and a brand-new account has
 * nothing saved at all. All of those mean Grow.
 *
 * @param value - Whatever was saved, or nothing.
 * @returns A theme the app has.
 */
export function normaliseTheme(value: string | null | undefined): Preferences['theme'] {
  switch (value) {
    case 'light':
    case 'dark':
    case 'fieldnotes':
      return value;
    default:
      return 'grow';
  }
} 